import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
} from '@mui/material';
import { Upload, FileSpreadsheet, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import coursesService from '../../../services/CoursesService';

const downloadTemplate = () => {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['cedula', 'id_curso'],
    ['1234567890', '665abc123def456789012345'],
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Matriculas');
  XLSX.writeFile(workbook, 'plantilla_matriculas_masivas.xlsx');
};

const statusColor = (status) => {
  if (status === 'assigned') return 'success';
  if (status === 'skipped') return 'warning';
  return 'error';
};

const statusLabel = (status) => {
  if (status === 'assigned') return 'Matriculado';
  if (status === 'skipped') return 'Omitido';
  return 'Error';
};

const BulkEnrollmentImport = () => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setError('');
    setResults([]);
    setSummary(null);
    setProgress(0);
    setProgressLabel('');
    setFile(selectedFile);
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError('');
    setResults([]);
    setSummary(null);
    setProgress(0);
    setProgressLabel('Subiendo archivo...');

    try {
      const result = await coursesService.bulkImportEnrollmentsFromExcel(
        file,
        undefined,
        (progressEvent) => {
          if (progressEvent.total) {
            const uploadPercent = Math.round((progressEvent.loaded / progressEvent.total) * 50);
            setProgress(uploadPercent);
            setProgressLabel(`Subiendo archivo... ${uploadPercent}%`);
          }
        }
      );

      setProgress(75);
      setProgressLabel('Procesando matrículas...');

      const importResults = result.results || [];
      setResults(importResults);
      setSummary({
        totalRows: result.totalRows || importResults.length,
        assigned: result.assigned || 0,
        skipped: result.skipped || 0,
        errors: result.errors || 0,
      });
      setProgress(100);
      setProgressLabel('Importación completada');
    } catch (importError) {
      const message =
        importError.response?.data ||
        importError.message ||
        'Error al importar las matrículas.';
      setError(typeof message === 'string' ? message : 'Error al importar las matrículas.');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResults([]);
    setSummary(null);
    setError('');
    setProgress(0);
    setProgressLabel('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FileSpreadsheet size={28} color="#4DABA4" />
          <Typography variant="h5" fontWeight={600}>
            Importar matrículas masivas
          </Typography>
        </Stack>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Sube un archivo Excel con las columnas <strong>cedula</strong> e <strong>id_curso</strong>.
          Cada fila matriculará al usuario directamente en el curso indicado.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Download size={18} />}
            onClick={downloadTemplate}
            sx={{ borderColor: '#4DABA4', color: '#4DABA4' }}
          >
            Descargar plantilla
          </Button>
          <Button
            variant="contained"
            component="label"
            startIcon={<Upload size={18} />}
            disabled={importing}
            sx={{ backgroundColor: '#4DABA4', '&:hover': { backgroundColor: '#3d9690' } }}
          >
            Seleccionar Excel
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Button variant="text" onClick={handleReset} disabled={importing}>
              Limpiar
            </Button>
          )}
        </Stack>

        {file && !summary && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Archivo listo: <strong>{file.name}</strong>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {importing && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {progressLabel}
            </Typography>
            <LinearProgress
              variant={progress > 0 && progress < 100 ? 'determinate' : 'indeterminate'}
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {summary && (
          <Alert severity={summary.errors > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
            Importación finalizada: {summary.assigned} matriculadas, {summary.skipped} omitidas,{' '}
            {summary.errors} con error (de {summary.totalRows} filas).
          </Alert>
        )}

        {file && !importing && !summary && (
          <Box sx={{ mb: 3, textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={handleImport}
              sx={{ backgroundColor: '#4DABA4', '&:hover': { backgroundColor: '#3d9690' } }}
            >
              Iniciar importación
            </Button>
          </Box>
        )}

        {results.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Fila</TableCell>
                  <TableCell>Cédula</TableCell>
                  <TableCell>ID Curso</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row, index) => {
                  const rowNumber = row.rowNumber ?? row.RowNumber;
                  const cedula = row.cedula ?? row.Cedula;
                  const courseId = row.courseId ?? row.CourseId;
                  const status = row.status ?? row.Status;
                  const message = row.message ?? row.Message;

                  return (
                    <TableRow key={`${rowNumber}-${index}`}>
                      <TableCell>{rowNumber}</TableCell>
                      <TableCell>{cedula}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{courseId}</TableCell>
                      <TableCell>
                        <Chip label={statusLabel(status)} color={statusColor(status)} size="small" />
                      </TableCell>
                      <TableCell>{message || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default BulkEnrollmentImport;
